import React from 'react';

type ScrollablePaginationOptions = {
    containerRef: React.RefObject<HTMLElement | null>;
    pageSize: number;
    page?: number;
    setPage?: React.Dispatch<React.SetStateAction<number>>;
    total?: number;
    lastPageCount?: number;
    isLoading?: boolean;
    isFetching?: boolean;
    resetDeps?: React.DependencyList;
    scrollOffset?: number;
    enabled?: boolean;
};

type ScrollablePaginationState = {
    page: number;
    hasMore: boolean;
    reset: () => void;
};

export const useScrollablePagination = (options: ScrollablePaginationOptions): ScrollablePaginationState => {
    const {
        containerRef,
        pageSize,
        page: controlledPage,
        setPage: controlledSetPage,
        total,
        lastPageCount,
        isLoading = false,
        isFetching = false,
        resetDeps = [],
        scrollOffset = 32,
        enabled = true,
    } = options;

    const [internalPage, setInternalPage] = React.useState(0);
    const page = controlledPage ?? internalPage;
    const setPage = controlledSetPage ?? setInternalPage;
    const requestInFlightRef = React.useRef(false);

    const hasMore = React.useMemo(() => {
        if (!enabled) return false;
        if (typeof total === 'number') {
            return (page + 1) * pageSize < total;
        }
        if (typeof lastPageCount === 'number') {
            return lastPageCount >= pageSize;
        }
        return true;
    }, [total, page, pageSize, lastPageCount]);

    const reset = React.useCallback(() => {
        setPage(0);
        const element = containerRef.current;
        if (element) {
            element.scrollTop = 0;
        }
    }, [containerRef, setPage]);

    React.useEffect(() => {
        reset();
    }, resetDeps);

    React.useEffect(() => {
        if (!isLoading && !isFetching) {
            requestInFlightRef.current = false;
        }
    }, [isLoading, isFetching]);

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element || !enabled) return;

        const getScrollableElements = (node: HTMLElement): HTMLElement[] => {
            const scrollables: HTMLElement[] = [];
            if (node.scrollHeight > node.clientHeight) {
                scrollables.push(node);
            }
            const children = node.querySelectorAll<HTMLElement>('*');
            for (const child of children) {
                const style = window.getComputedStyle(child);
                const overflowY = style.overflowY;
                if ((overflowY === 'auto' || overflowY === 'scroll') && child.scrollHeight > child.clientHeight) {
                    scrollables.push(child);
                }
            }
            return scrollables.length ? scrollables : [node];
        };

        const scrollElements = getScrollableElements(element);

        const handleScroll = (): void => {
            if (requestInFlightRef.current || isLoading || isFetching || !hasMore) return;
            for (const scrollElement of scrollElements) {
                const { scrollTop, scrollHeight, clientHeight } = scrollElement;
                const isNearBottom = scrollTop + clientHeight >= scrollHeight - scrollOffset;
                if (isNearBottom) {
                    requestInFlightRef.current = true;
                    setPage((prev) => prev + 1);
                    return;
                }
            }
        };

        const eventOptions: AddEventListenerOptions = { passive: true };
        scrollElements.forEach((scrollElement) => scrollElement.addEventListener('scroll', handleScroll, eventOptions));

        return () => {
            scrollElements.forEach((scrollElement) =>
                scrollElement.removeEventListener('scroll', handleScroll, eventOptions)
            );
        };
    }, [containerRef, enabled, hasMore, isLoading, isFetching, scrollOffset, setPage]);

    return { page, hasMore, reset };
};
